import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, Pause, RotateCcw, Coffee, Brain, Settings, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { usePomodoro } from "@/contexts/PomodoroContext";

export const PomodoroTimer = () => {
  const {
    mode,
    timeLeft,
    isRunning,
    isComplete,
    sessionsCompleted,
    config,
    toggleTimer,
    resetTimer,
    setMode,
    updateConfig,
  } = usePomodoro();

  const [showSettings, setShowSettings] = useState(false);
  const [focusMinutes, setFocusMinutes] = useState(Math.floor(config.focus / 60));
  const [shortBreakMinutes, setShortBreakMinutes] = useState(Math.floor(config.shortBreak / 60));
  const [longBreakMinutes, setLongBreakMinutes] = useState(Math.floor(config.longBreak / 60));

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleModeChange = (newMode: "focus" | "shortBreak" | "longBreak") => {
    setMode(newMode);
  };

  const saveSettings = () => {
    const newConfig = {
      focus: Math.max(1, focusMinutes) * 60,
      shortBreak: Math.max(1, shortBreakMinutes) * 60,
      longBreak: Math.max(1, longBreakMinutes) * 60,
    };
    updateConfig(newConfig);
    setShowSettings(false);
  };

  const progress = ((config[mode] - timeLeft) / config[mode]) * 100;

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <div className="flex gap-2">
        <Button
          variant={mode === "focus" ? "default" : "outline"}
          size="sm"
          onClick={() => handleModeChange("focus")}
          className="flex-1 gap-1.5"
        >
          <Brain className="h-4 w-4" />
          Focus
        </Button>
        <Button
          variant={mode === "shortBreak" ? "default" : "outline"}
          size="sm"
          onClick={() => handleModeChange("shortBreak")}
          className="flex-1 gap-1.5"
        >
          <Coffee className="h-4 w-4" />
          Short
        </Button>
        <Button
          variant={mode === "longBreak" ? "default" : "outline"}
          size="sm"
          onClick={() => handleModeChange("longBreak")}
          className="flex-1 gap-1.5"
        >
          <Coffee className="h-4 w-4" />
          Long
        </Button>
      </div>

      {/* Timer Display */}
      <Card className="p-6 relative overflow-hidden">
        {/* Progress background */}
        <div 
          className={cn(
            "absolute inset-0 transition-all duration-1000",
            mode === "focus" ? "bg-primary/10" : "bg-accent/10"
          )}
          style={{ width: `${progress}%` }}
        />
        
        <div className="relative text-center space-y-4">
          <div className={cn(
            "text-6xl font-mono font-bold tracking-wider",
            isComplete && "text-primary animate-pulse"
          )}>
            {isComplete ? "Time's Up!" : formatTime(timeLeft)}
          </div>
          
          <div className="text-sm text-muted-foreground capitalize">
            {mode === "focus" ? "Focus Time" : mode === "shortBreak" ? "Short Break" : "Long Break"}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => resetTimer(mode)}
              className="h-10 w-10 rounded-full"
              aria-label="Reset timer"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            
            <Button
              variant="default"
              size="lg"
              onClick={toggleTimer}
              className="h-14 w-14 rounded-full"
              aria-label={isRunning ? "Pause timer" : "Start timer"}
            >
              {isRunning ? (
                <Pause className="h-6 w-6" />
              ) : (
                <Play className="h-6 w-6 ml-0.5" />
              )}
            </Button>
            
            <div className="h-10 w-10" /> {/* Spacer for symmetry */}
          </div>
        </div>
      </Card>

      {/* Sessions Counter */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Sessions completed: <span className="font-semibold text-foreground">{sessionsCompleted}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {4 - (sessionsCompleted % 4)} more until long break
        </p>
      </div>

      {/* Custom Duration Settings */}
      <Collapsible open={showSettings} onOpenChange={setShowSettings}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full gap-2">
            <Settings className="h-4 w-4" />
            Customize Durations
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="focus-duration" className="text-xs">Focus (min)</Label>
              <Input
                id="focus-duration"
                type="number"
                min={1}
                max={120}
                value={focusMinutes}
                onChange={(e) => setFocusMinutes(parseInt(e.target.value) || 1)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="short-break" className="text-xs">Short (min)</Label>
              <Input
                id="short-break"
                type="number"
                min={1}
                max={60}
                value={shortBreakMinutes}
                onChange={(e) => setShortBreakMinutes(parseInt(e.target.value) || 1)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="long-break" className="text-xs">Long (min)</Label>
              <Input
                id="long-break"
                type="number"
                min={1}
                max={120}
                value={longBreakMinutes}
                onChange={(e) => setLongBreakMinutes(parseInt(e.target.value) || 1)}
                className="h-9"
              />
            </div>
          </div>
          <Button onClick={saveSettings} size="sm" className="w-full gap-2">
            <Check className="h-4 w-4" />
            Save Settings
          </Button>
        </CollapsibleContent>
      </Collapsible>

      {/* Tips */}
      <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-border">
        <p className="font-medium text-foreground">Pomodoro Technique:</p>
        <p>• {Math.floor(config.focus / 60)} min focus → {Math.floor(config.shortBreak / 60)} min break</p>
        <p>• Every 4 sessions → {Math.floor(config.longBreak / 60)} min break</p>
        <p>• Stay focused, avoid distractions!</p>
      </div>
    </div>
  );
};